import { useEffect, useState } from "react";
import { Clock, Lock, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { SelettoreData } from "@/components/SelettoreData";
import { useAuth } from "@/context/AuthContext";

const oggi = () => new Date().toISOString().slice(0, 10);
const VUOTO = {
  data: oggi(), agenzia: "", nominativo: "", numero_pratica: "", origine: "antonella", nota: "",
  esito_chiamata: "", data_ultimo_contatto: "", data_ricontatto: "", ora_ricontatto: "",
  preventivo: { importo: "", durata: "", rata: "", tan: "", taeg: "", importo_estinzione: "", note: "" },
  documenti_mancanti: [], stato: "Da chiamare", caricata: false, caricata_il: "", caricata_importo: "",
  liquidata: false, liquidata_il: "",
};
const cls = "w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#0018A8]";
const quando = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });

function Field({ label, children, span }) {
  return (
    <div className={span ? `col-span-${span}` : ""}>
      <label className="block text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

/** Modale "Gestione CQ": 4 schede (una per tipo di file), per ora attiva solo "Report CQ". */
export function GestioneCqModal({ open, meta, contatto, onClose, onSaved }) {
  const { user } = useAuth();
  const [tab, setTab] = useState("report");
  const [f, setF] = useState(VUOTO);
  const [note, setNote] = useState([]);
  const [salvo, setSalvo] = useState(false);
  const [mostraNote, setMostraNote] = useState(false);
  const master = user?.role === "MASTER" || user?.role === "ADMIN";

  useEffect(() => {
    if (!open) return;
    if (contatto) {
      setF({ ...VUOTO, ...contatto, nota: "", numero_pratica: contatto.numero_pratica || "",
        preventivo: { ...VUOTO.preventivo, ...Object.fromEntries(Object.entries(contatto.preventivo || {}).map(([k, v]) => [k, v ?? ""])) },
        caricata_importo: contatto.caricata_importo ?? "", esito_chiamata: contatto.esito_chiamata || "",
        data_ultimo_contatto: contatto.data_ultimo_contatto || "", data_ricontatto: contatto.data_ricontatto || "",
        ora_ricontatto: contatto.ora_ricontatto || "", caricata_il: contatto.caricata_il || "", liquidata_il: contatto.liquidata_il || "" });
      setNote(contatto.note || []);
      setMostraNote((contatto.note || []).length > 0);
    } else { setF({ ...VUOTO, data: oggi() }); setNote([]); setMostraNote(false); }
    setTab("report");
  }, [open, contatto]);

  if (!open) return null;
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e?.target ? e.target.value : e }));
  const setP = (k) => (e) => setF((x) => ({ ...x, preventivo: { ...x.preventivo, [k]: e.target.value } }));
  const toggleDoc = (k) => setF((x) => ({ ...x, documenti_mancanti: x.documenti_mancanti.includes(k)
    ? x.documenti_mancanti.filter((d) => d !== k) : [...x.documenti_mancanti, k] }));

  const salva = async (e) => {
    e.preventDefault();
    setSalvo(true);
    const num = (v) => (v === "" || v === null || v === undefined ? null : Number(String(v).replace(",", ".")));
    const body = { ...f, caricata_importo: num(f.caricata_importo), numero_pratica: f.numero_pratica || null,
      data_ultimo_contatto: f.data_ultimo_contatto || null, data_ricontatto: f.data_ricontatto || null,
      ora_ricontatto: f.ora_ricontatto || null, caricata_il: f.caricata_il || null, liquidata_il: f.liquidata_il || null,
      esito_chiamata: f.esito_chiamata || null,
      preventivo: Object.fromEntries(Object.entries(f.preventivo).map(([k, v]) => [k, k === "note" ? v : num(v)])) };
    try {
      if (contatto?.id) await api.put(`/cq/report/${contatto.id}`, body);
      else await api.post("/cq/report", body);
      toast.success(contatto?.id ? `${f.nominativo} aggiornato` : `${f.nominativo} inserito`);
      onSaved?.(); onClose();
    } catch (err) { toast.error(apiError(err)); } finally { setSalvo(false); }
  };
  const elimina = async () => {
    if (!window.confirm(`Eliminare definitivamente ${f.nominativo}?`)) return;
    try { await api.delete(`/cq/report/${contatto.id}`); toast.success("Contatto eliminato"); onSaved?.(); onClose(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="fixed inset-0 z-[1300] bg-slate-900/50 flex items-start justify-center overflow-y-auto p-4"
      data-testid="gestione-cq-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form onSubmit={salva} className="bg-white rounded-xl shadow-2xl w-full max-w-5xl my-4">
        <div className="flex items-center justify-between px-6 pt-5">
          <h2 className="text-lg font-bold text-slate-900">Gestione CQ</h2>
          <button type="button" onClick={onClose} data-testid="cq-modal-chiudi" className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="flex gap-1 px-6 mt-3 border-b border-slate-200">
          {(meta?.tipi || []).map((t) => (
            <button key={t.key} type="button" disabled={!t.attivo} data-testid={`cq-tab-${t.key}`}
              onClick={() => t.attivo && setTab(t.key)} title={t.attivo ? "" : "In arrivo"}
              className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px inline-flex items-center gap-1.5 transition-colors duration-150
                ${tab === t.key ? "border-[#0018A8] text-[#0018A8]" : "border-transparent text-slate-500"}
                ${t.attivo ? "hover:text-[#0018A8]" : "opacity-50 cursor-not-allowed"}`}>
              {!t.attivo && <Lock size={12} />} {t.label}
            </button>
          ))}
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <Field label="Data *"><SelettoreData value={f.data} onChange={set("data")} testId="cq-data" /></Field>
          <Field label="Agenzia *">
            <select className={cls} value={f.agenzia} onChange={set("agenzia")} required data-testid="cq-agenzia">
              <option value="">—</option>
              {(meta?.agenzie || []).map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </Field>
          <Field label="Nominativo *"><input className={cls} value={f.nominativo} onChange={set("nominativo")} required data-testid="cq-nominativo" /></Field>
          <Field label="N. pratica (facoltativo)"><input className={cls} value={f.numero_pratica} onChange={set("numero_pratica")} data-testid="cq-numero-pratica" placeholder="per l'incrocio col CRM" /></Field>

          <Field label="Origine *" span={2}>
            <div className="flex gap-2">
              {(meta?.origini || []).map((o) => (
                <button key={o.key} type="button" onClick={() => setF((x) => ({ ...x, origine: o.key }))} data-testid={`cq-origine-${o.key}`}
                  className={`flex-1 px-3 py-2 rounded-md text-sm font-semibold border transition-colors duration-150 ${f.origine === o.key
                    ? "bg-[#0018A8] text-white border-[#0018A8]" : "bg-white text-slate-600 border-slate-300 hover:border-[#0018A8]"}`}>{o.label}</button>
              ))}
            </div>
          </Field>
          <Field label="Stato pratica *">
            <select className={cls} value={f.stato} onChange={set("stato")} data-testid="cq-stato">
              {(meta?.stati || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Esito ultima chiamata">
            <input className={cls} value={f.esito_chiamata} onChange={set("esito_chiamata")} data-testid="cq-esito" placeholder="es. non risponde, richiamare" />
          </Field>

          <Field label="Data ultimo contatto"><SelettoreData value={f.data_ultimo_contatto} onChange={set("data_ultimo_contatto")} testId="cq-data-contatto" /></Field>
          <Field label="Data ricontatto (scadenziere)"><SelettoreData value={f.data_ricontatto} onChange={set("data_ricontatto")} testId="cq-data-ricontatto" /></Field>
          <Field label="Ora ricontatto"><input type="time" className={cls} value={f.ora_ricontatto} onChange={set("ora_ricontatto")} data-testid="cq-ora-ricontatto" /></Field>
          <div />

          <section className="col-span-full rounded-lg border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">Indicazioni / note</h3>
              {note.length > 0 && (
                <button type="button" onClick={() => setMostraNote((v) => !v)} data-testid="cq-note-toggle"
                  className="inline-flex items-center gap-1 text-xs text-[#0018A8] hover:underline">
                  <Clock size={12} /> {mostraNote ? "Nascondi" : "Mostra"} cronologia ({note.length})
                </button>
              )}
            </div>
            {mostraNote && note.length > 0 && (
              <ul className="mb-3 max-h-48 overflow-y-auto space-y-1.5" data-testid="cq-note-storico">
                {[...note].reverse().map((n, i) => (
                  <li key={i} className="text-sm bg-slate-50 border border-slate-100 rounded px-3 py-1.5">
                    <span className="text-[11px] text-slate-400 font-mono mr-2">{quando(n.il)} · {n.da}</span>
                    <span className="text-slate-800 whitespace-pre-wrap">{n.testo}</span>
                  </li>
                ))}
              </ul>
            )}
            <textarea className={`${cls} min-h-[70px]`} value={f.nota} onChange={set("nota")} data-testid="cq-nota"
              placeholder="Aggiungi una nota: si somma alla cronologia, le precedenti restano" />
          </section>

          <section className="col-span-full md:col-span-2 rounded-lg border border-slate-200 p-4">
            <h3 className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-3">Preventivo proposto (piano finanziario)</h3>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Importo richiesto €"><input className={cls} inputMode="decimal" value={f.preventivo.importo} onChange={setP("importo")} data-testid="cq-prev-importo" /></Field>
              <Field label="Durata (rate)"><input className={cls} inputMode="numeric" value={f.preventivo.durata} onChange={setP("durata")} data-testid="cq-prev-durata" /></Field>
              <Field label="Rata €"><input className={cls} inputMode="decimal" value={f.preventivo.rata} onChange={setP("rata")} data-testid="cq-prev-rata" /></Field>
              <Field label="TAN %"><input className={cls} inputMode="decimal" value={f.preventivo.tan} onChange={setP("tan")} data-testid="cq-prev-tan" /></Field>
              <Field label="TAEG %"><input className={cls} inputMode="decimal" value={f.preventivo.taeg} onChange={setP("taeg")} data-testid="cq-prev-taeg" /></Field>
              <Field label="Estinzione in corso €"><input className={cls} inputMode="decimal" value={f.preventivo.importo_estinzione} onChange={setP("importo_estinzione")} data-testid="cq-prev-estinzione" /></Field>
              <Field label="Note preventivo" span={3}><input className={cls} value={f.preventivo.note || ""} onChange={setP("note")} data-testid="cq-prev-note" /></Field>
            </div>
          </section>

          <section className="col-span-full md:col-span-2 rounded-lg border border-slate-200 p-4">
            <h3 className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-3">Documenti mancanti</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {(meta?.documenti || []).map((d) => (
                <label key={d.key} className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
                  <input type="checkbox" checked={f.documenti_mancanti.includes(d.key)} onChange={() => toggleDoc(d.key)}
                    data-testid={`cq-doc-${d.key}`} className="h-4 w-4 accent-[#C8102E]" /> {d.label}
                </label>
              ))}
            </div>
          </section>

          <section className="col-span-full grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={`rounded-lg border p-4 ${f.caricata ? "border-[#1D4ED8] bg-blue-50/40" : "border-slate-200"}`}>
              <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800 cursor-pointer">
                <input type="checkbox" checked={f.caricata} data-testid="cq-caricata" className="h-4 w-4 accent-[#1D4ED8]"
                  onChange={(e) => setF((x) => ({ ...x, caricata: e.target.checked, stato: e.target.checked && !x.liquidata ? "Caricata" : x.stato,
                    liquidata: e.target.checked ? x.liquidata : false }))} /> CQ caricata
              </label>
              {f.caricata && (
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <Field label="Data caricamento"><SelettoreData value={f.caricata_il} onChange={set("caricata_il")} testId="cq-caricata-il" /></Field>
                  <Field label="Importo caricato € *"><input className={cls} inputMode="decimal" value={f.caricata_importo} onChange={set("caricata_importo")} required data-testid="cq-caricata-importo" /></Field>
                </div>
              )}
            </div>
            <div className={`rounded-lg border p-4 ${f.liquidata ? "border-[#0E7490] bg-teal-50/40" : "border-slate-200"} ${!f.caricata ? "opacity-50" : ""}`}>
              <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800 cursor-pointer">
                <input type="checkbox" checked={f.liquidata} disabled={!f.caricata} data-testid="cq-liquidata" className="h-4 w-4 accent-[#0E7490]"
                  onChange={(e) => setF((x) => ({ ...x, liquidata: e.target.checked, stato: e.target.checked ? "Liquidata" : "Caricata" }))} /> CQ liquidata
                <span className="text-xs text-slate-500 font-normal">(importo = caricato)</span>
              </label>
              {f.liquidata && (
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <Field label="Data liquidazione"><SelettoreData value={f.liquidata_il} onChange={set("liquidata_il")} testId="cq-liquidata-il" /></Field>
                </div>
              )}
            </div>
          </section>
        </div>

        <div className="flex items-center justify-between px-6 pb-6">
          <div>
            {contatto?.id && master && (
              <button type="button" onClick={elimina} data-testid="cq-elimina"
                className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-[#C8102E]"><Trash2 size={14} /> Elimina</button>
            )}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border border-slate-300 text-sm text-slate-600">Annulla</button>
            <button type="submit" disabled={salvo} data-testid="cq-salva"
              className="px-5 py-2 rounded-md bg-[#0018A8] text-white text-sm font-semibold hover:bg-[#001480] disabled:opacity-50">
              {salvo ? "Salvataggio…" : contatto?.id ? "Salva modifiche" : "Inserisci"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
